import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi } from "../../api/endpoints";
import type { AiApiProvider, AiModel, OpenRouterProviderRoute } from "../../types/domain";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { IcoEdit, IcoPlus, IcoTrash } from "../../components/icons";

type SettingsTab = "general" | "models";

type ModelDraft = {
  name: string;
  apiProvider: AiApiProvider;
  modelId: string;
  primaryProvider: string;
  fallbackProvider: string;
};

const EMPTY_MODEL: ModelDraft = {
  name: "",
  apiProvider: "openrouter",
  modelId: "",
  primaryProvider: "",
  fallbackProvider: "",
};

const API_PROVIDER_LABEL: Record<AiApiProvider, string> = {
  openrouter: "OpenRouter",
  cerebras: "Cerebras напрямую",
};

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : "Не удалось сохранить изменения";
}

const OPENROUTER_MODEL_ID = /^[a-z0-9][a-z0-9._-]*\/[a-z0-9][a-z0-9._:/-]*$/i;

function providerLabel(provider: OpenRouterProviderRoute): string {
  const details = [provider.name];
  if (provider.quantization) details.push(provider.quantization.toUpperCase());
  details.push(provider.code);
  return details.join(" · ");
}

export function AppSettingsPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<SettingsTab>("general");
  const [grammarModelId, setGrammarModelId] = useState("");
  const [additionalPrompt, setAdditionalPrompt] = useState("");
  const [editingId, setEditingId] = useState<string | "new" | null>(null);
  const [modelDraft, setModelDraft] = useState<ModelDraft>(EMPTY_MODEL);
  const [deleteModel, setDeleteModel] = useState<AiModel | null>(null);
  const settingsDraftTouched = useRef(false);

  const settingsQuery = useQuery({
    queryKey: ["admin-ai-settings"],
    queryFn: () => adminApi.getAiSettings(),
  });

  useEffect(() => {
    if (!settingsQuery.data || settingsDraftTouched.current) return;
    setGrammarModelId(settingsQuery.data.settings.grammarModelId ?? "");
    setAdditionalPrompt(settingsQuery.data.settings.grammarAdditionalPrompt);
  }, [settingsQuery.data]);

  const models = settingsQuery.data?.models ?? [];
  const selectedModel = useMemo(
    () => models.find((model) => model.id === grammarModelId) ?? null,
    [grammarModelId, models],
  );
  const settingsDirty = Boolean(
    settingsQuery.data &&
      (grammarModelId !== (settingsQuery.data.settings.grammarModelId ?? "") ||
        additionalPrompt !== settingsQuery.data.settings.grammarAdditionalPrompt),
  );

  const settingsMutation = useMutation({
    mutationFn: () =>
      adminApi.patchAiSettings({
        grammarModelId,
        grammarAdditionalPrompt: additionalPrompt,
      }),
    onSuccess: async ({ settings }) => {
      settingsDraftTouched.current = false;
      setGrammarModelId(settings.grammarModelId ?? "");
      setAdditionalPrompt(settings.grammarAdditionalPrompt);
      await queryClient.invalidateQueries({ queryKey: ["admin-ai-settings"] });
    },
  });

  const modelMutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: modelDraft.name,
        apiProvider: modelDraft.apiProvider,
        modelId: modelDraft.modelId,
        primaryProvider:
          modelDraft.apiProvider === "openrouter" ? modelDraft.primaryProvider || null : null,
        fallbackProvider:
          modelDraft.apiProvider === "openrouter" ? modelDraft.fallbackProvider || null : null,
      };
      return editingId === "new"
        ? adminApi.createAiModel(payload)
        : adminApi.patchAiModel(String(editingId), payload);
    },
    onSuccess: async () => {
      setEditingId(null);
      setModelDraft(EMPTY_MODEL);
      await queryClient.invalidateQueries({ queryKey: ["admin-ai-settings"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.removeAiModel(id),
    onSuccess: async () => {
      setDeleteModel(null);
      await queryClient.invalidateQueries({ queryKey: ["admin-ai-settings"] });
    },
  });

  const startEdit = (model: AiModel) => {
    setEditingId(model.id);
    setModelDraft({
      name: model.name,
      apiProvider: model.apiProvider,
      modelId: model.modelId,
      primaryProvider: model.primaryProvider ?? "",
      fallbackProvider: model.fallbackProvider ?? "",
    });
  };

  return (
    <div className="admin-page app-settings-page">
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Настройки</h1>
          <div className="admin-sub">
            Общие параметры приложения и модели OpenRouter или Cerebras.
          </div>
        </div>
      </div>

      <div className="settings-tabs" role="tablist" aria-label="Разделы настроек">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "general"}
          className={tab === "general" ? "active" : ""}
          onClick={() => setTab("general")}
        >
          Общие
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "models"}
          className={tab === "models" ? "active" : ""}
          onClick={() => setTab("models")}
        >
          Модели
        </button>
      </div>

      {settingsQuery.isLoading && <div className="admin-empty">Загружаем настройки…</div>}
      {settingsQuery.isError && (
        <div className="admin-error">Не удалось загрузить настройки. {errorText(settingsQuery.error)}</div>
      )}

      {settingsQuery.data && tab === "general" && (
        <section className="settings-card" aria-labelledby="grammar-settings-title">
          <div className="settings-card-head">
            <div>
              <h2 id="grammar-settings-title">Проверка текста</h2>
              <p>Модель исправляет орфографию, грамматику и пунктуацию в редакторе карточки.</p>
            </div>
          </div>

          <label className="settings-field">
            <span className="form-label">Модель</span>
            <select
              className="form-select"
              value={grammarModelId}
              onChange={(event) => {
                settingsDraftTouched.current = true;
                settingsMutation.reset();
                setGrammarModelId(event.target.value);
              }}
            >
              {models.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name} · {model.modelId} · {API_PROVIDER_LABEL[model.apiProvider]}
                </option>
              ))}
            </select>
          </label>

          {selectedModel && (
            <div className="settings-route-summary">
              <span>Маршрут</span>
              {selectedModel.apiProvider === "cerebras" ? (
                <span>прямое подключение к Cerebras</span>
              ) : selectedModel.primaryProvider ? (
                <code>
                  {selectedModel.primaryProvider}
                  {selectedModel.fallbackProvider ? ` → ${selectedModel.fallbackProvider}` : ""}
                </code>
              ) : (
                <span>автоматический выбор OpenRouter</span>
              )}
            </div>
          )}

          <label className="settings-field">
            <span className="form-label">Дополнительный промпт</span>
            <textarea
              className="form-textarea settings-prompt"
              value={additionalPrompt}
              maxLength={10_000}
              placeholder="Например: разбивай текст на небольшие абзацы по 2–4 предложения."
              onChange={(event) => {
                settingsDraftTouched.current = true;
                settingsMutation.reset();
                setAdditionalPrompt(event.target.value);
              }}
            />
            <span className="settings-help">
              Инструкция дополняет базовые правила корректора. Формат JSON приложение добавляет само.
            </span>
          </label>

          <div className="settings-actions">
            {settingsMutation.isError && (
              <span className="admin-error">{errorText(settingsMutation.error)}</span>
            )}
            {settingsMutation.isSuccess && !settingsMutation.isPending && !settingsDirty && (
              <span className="settings-saved">Сохранено</span>
            )}
            <button
              type="button"
              className="btn-primary"
              disabled={!grammarModelId || !settingsDirty || settingsMutation.isPending}
              onClick={() => settingsMutation.mutate()}
            >
              {settingsMutation.isPending ? "Сохраняем…" : "Сохранить настройки"}
            </button>
          </div>
        </section>
      )}

      {settingsQuery.data && tab === "models" && (
        <div className="settings-models">
          <div className="settings-section-head">
            <div>
              <h2>Модели</h2>
              <p>Добавьте модель OpenRouter или прямую модель Cerebras.</p>
            </div>
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                setEditingId("new");
                setModelDraft(EMPTY_MODEL);
              }}
            >
              <IcoPlus size={14} /> Добавить модель
            </button>
          </div>

          {editingId && (
            <ModelForm
              key={editingId}
              draft={modelDraft}
              editing={editingId !== "new"}
              busy={modelMutation.isPending}
              error={modelMutation.isError ? errorText(modelMutation.error) : null}
              onChange={setModelDraft}
              onCancel={() => {
                setEditingId(null);
                setModelDraft(EMPTY_MODEL);
              }}
              onSubmit={() => modelMutation.mutate()}
            />
          )}

          <div className="settings-model-list">
            {models.map((model) => {
              const active = settingsQuery.data.settings.grammarModelId === model.id;
              return (
                <article className="settings-model-card" key={model.id}>
                  <div className="settings-model-main">
                    <div className="settings-model-name">
                      {model.name}
                      <span className="settings-provider-badge">
                        {API_PROVIDER_LABEL[model.apiProvider]}
                      </span>
                      {active && <span className="settings-active-badge">Используется</span>}
                    </div>
                    <code>{model.modelId}</code>
                    <div className="settings-model-route">
                      {model.apiProvider === "cerebras" ? (
                        "Прямой вызов Cerebras API"
                      ) : model.primaryProvider ? (
                        <>
                          Основной: <b>{model.primaryProvider}</b>
                          {model.fallbackProvider && (
                            <>
                              <span>Запасной: </span><b>{model.fallbackProvider}</b>
                            </>
                          )}
                        </>
                      ) : (
                        "Провайдера выбирает OpenRouter"
                      )}
                    </div>
                  </div>
                  <div className="settings-model-actions">
                    <button type="button" className="btn-ghost" onClick={() => startEdit(model)}>
                      <IcoEdit size={14} /> Редактировать
                    </button>
                    <button
                      type="button"
                      className="btn-ghost settings-delete-btn"
                      disabled={active}
                      title={active ? "Сначала выберите другую модель" : "Удалить модель"}
                      onClick={() => setDeleteModel(model)}
                    >
                      <IcoTrash size={14} /> Удалить
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      )}

      {deleteModel && (
        <ConfirmDialog
          title="Удалить модель?"
          message={`${deleteModel.name} исчезнет из списка доступных моделей.`}
          body={
            deleteMutation.isError ? (
              <div className="admin-error">{errorText(deleteMutation.error)}</div>
            ) : undefined
          }
          busy={deleteMutation.isPending}
          onClose={() => {
            deleteMutation.reset();
            setDeleteModel(null);
          }}
          onConfirm={() => deleteMutation.mutate(deleteModel.id)}
        />
      )}
    </div>
  );
}

function ModelForm({
  draft,
  editing,
  busy,
  error,
  onChange,
  onCancel,
  onSubmit,
}: {
  draft: ModelDraft;
  editing: boolean;
  busy: boolean;
  error: string | null;
  onChange: (draft: ModelDraft) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const normalizedModelId = draft.modelId.trim();
  const lookupCandidate =
    draft.apiProvider === "openrouter" && OPENROUTER_MODEL_ID.test(normalizedModelId)
      ? normalizedModelId
      : "";
  const [lookupModelId, setLookupModelId] = useState("");
  const nameManuallyEdited = useRef(editing);
  const appliedLookup = useRef("");

  useEffect(() => {
    if (!lookupCandidate) {
      setLookupModelId("");
      return;
    }
    const timer = window.setTimeout(() => setLookupModelId(lookupCandidate), 500);
    return () => window.clearTimeout(timer);
  }, [lookupCandidate]);

  const lookupQuery = useQuery({
    queryKey: ["openrouter-model-lookup", lookupModelId],
    queryFn: () => adminApi.lookupOpenRouterModel(lookupModelId),
    enabled: Boolean(lookupModelId),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const lookup =
    lookupQuery.data && lookupModelId === normalizedModelId ? lookupQuery.data : null;
  const lookupPending =
    Boolean(lookupCandidate) &&
    (lookupModelId !== normalizedModelId || lookupQuery.isFetching);
  const lookupError =
    lookupModelId === normalizedModelId && lookupQuery.isError ? lookupQuery.error : null;

  useEffect(() => {
    if (!lookup || appliedLookup.current === normalizedModelId) return;
    appliedLookup.current = normalizedModelId;
    const providerCodes = new Set(lookup.providers.map((provider) => provider.code));
    const primaryProvider = providerCodes.has(draft.primaryProvider)
      ? draft.primaryProvider
      : "";
    const fallbackProvider =
      primaryProvider && providerCodes.has(draft.fallbackProvider)
        ? draft.fallbackProvider
        : "";
    onChange({
      ...draft,
      name: nameManuallyEdited.current ? draft.name : lookup.model.name,
      primaryProvider,
      fallbackProvider,
    });
  }, [draft, lookup, normalizedModelId, onChange]);

  const canSubmit =
    draft.name.trim().length > 0 &&
    (draft.apiProvider === "openrouter"
      ? Boolean(lookup)
      : /^[a-z0-9][a-z0-9._-]*$/i.test(draft.modelId.trim())) &&
    (draft.apiProvider === "cerebras" ||
      ((!draft.fallbackProvider.trim() || draft.primaryProvider.trim().length > 0) &&
        (!draft.fallbackProvider.trim() ||
          draft.primaryProvider.trim() !== draft.fallbackProvider.trim())));

  const update = (field: keyof ModelDraft, value: string) => {
    if (field === "name") nameManuallyEdited.current = true;
    onChange({ ...draft, [field]: value });
  };

  const updateModelId = (modelId: string) => {
    appliedLookup.current = "";
    if (draft.apiProvider === "openrouter") nameManuallyEdited.current = false;
    onChange({
      ...draft,
      name: draft.apiProvider === "openrouter" ? "" : draft.name,
      modelId,
      primaryProvider: "",
      fallbackProvider: "",
    });
  };

  return (
    <section className="settings-card settings-model-form" aria-label={editing ? "Редактирование модели" : "Новая модель"}>
      <h3>{editing ? "Редактировать модель" : "Новая модель"}</h3>
      <div className="settings-form-grid">
        <label className="settings-field">
          <span className="form-label">Источник API</span>
          <select
            className="form-select"
            value={draft.apiProvider}
            onChange={(event) => {
              const apiProvider = event.target.value as AiApiProvider;
              onChange({
                ...draft,
                apiProvider,
                name: "",
                modelId: "",
                primaryProvider: apiProvider === "cerebras" ? "" : draft.primaryProvider,
                fallbackProvider: apiProvider === "cerebras" ? "" : draft.fallbackProvider,
              });
              appliedLookup.current = "";
              nameManuallyEdited.current = false;
            }}
          >
            <option value="openrouter">OpenRouter</option>
            <option value="cerebras">Cerebras напрямую</option>
          </select>
        </label>
        <label className="settings-field">
          <span className="form-label">
            {draft.apiProvider === "openrouter" ? "Код модели OpenRouter" : "ID модели Cerebras"}
          </span>
          <input
            className="form-input"
            value={draft.modelId}
            placeholder={
              draft.apiProvider === "openrouter" ? "google/gemini-2.5-flash" : "gpt-oss-120b"
            }
            autoCapitalize="none"
            spellCheck={false}
            onChange={(event) => updateModelId(event.target.value)}
          />
        </label>
        {draft.apiProvider === "openrouter" && (
          <div className="settings-lookup-status" aria-live="polite">
            {!lookupCandidate && "Введите полный код модели OpenRouter."}
            {lookupPending && "Проверяем код в OpenRouter…"}
            {lookupError && (
              <span className="admin-error">{errorText(lookupError)}</span>
            )}
            {lookup && !lookupQuery.isFetching && (
              <span className="settings-lookup-success">
                Модель найдена. Доступно маршрутов: {lookup.providers.length}.
              </span>
            )}
          </div>
        )}
        <label className="settings-field">
          <span className="form-label">Название</span>
          <input
            className="form-input"
            value={draft.name}
            placeholder="Gemini 2.5 Flash"
            onChange={(event) => update("name", event.target.value)}
          />
        </label>
        {draft.apiProvider === "openrouter" && lookup && (
          <>
            <label className="settings-field">
              <span className="form-label">Основной провайдер</span>
              <select
                className="form-select"
                value={draft.primaryProvider}
                onChange={(event) => {
                  const primaryProvider = event.target.value;
                  onChange({
                    ...draft,
                    primaryProvider,
                    fallbackProvider:
                      primaryProvider && primaryProvider !== draft.fallbackProvider
                        ? draft.fallbackProvider
                        : "",
                  });
                }}
              >
                <option value="">Автоматический выбор OpenRouter</option>
                {lookup.providers.map((provider) => (
                  <option key={provider.code} value={provider.code}>
                    {providerLabel(provider)}
                  </option>
                ))}
              </select>
            </label>
            <label className="settings-field">
              <span className="form-label">Запасной провайдер</span>
              <select
                className="form-select"
                value={draft.fallbackProvider}
                disabled={!draft.primaryProvider}
                onChange={(event) => update("fallbackProvider", event.target.value)}
              >
                <option value="">Без запасного провайдера</option>
                {lookup.providers.map((provider) => (
                  <option
                    key={provider.code}
                    value={provider.code}
                    disabled={provider.code === draft.primaryProvider}
                  >
                    {providerLabel(provider)}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}
      </div>
      <p className="settings-help">
        {draft.apiProvider === "openrouter"
          ? "Маршруты необязательны. Если указан основной, LaunchLab не выйдет за пределы заданного списка."
          : "LaunchLab вызовет эту модель напрямую через api.cerebras.ai."}
      </p>
      {error && <div className="admin-error">{error}</div>}
      <div className="settings-actions">
        <button type="button" className="btn-ghost" disabled={busy} onClick={onCancel}>Отмена</button>
        <button type="button" className="btn-primary" disabled={!canSubmit || busy} onClick={onSubmit}>
          {busy ? "Сохраняем…" : editing ? "Сохранить модель" : "Добавить модель"}
        </button>
      </div>
    </section>
  );
}
